import { useNavigate, useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useState } from "react"
import { useMutation } from "urql"
import { useShallow } from "zustand/shallow"

import { ClonePurchaseOrderDocument, OrderStatus, SortingOrder } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { PurchaseOrderShipToFormValue } from "@/screens/Procurement/PurchaseOrder/types"
import { PurchaseOrderSearchCategory } from "@/screens/Procurement/types/enums"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const CLONABLE_STATUSES = [OrderStatus.Sent, OrderStatus.Confirmed] as const

type ClonableStatus = (typeof CLONABLE_STATUSES)[number]

interface CloneActionReturn {
  canClone: boolean
  handleClone: () => void
  handlePreClone: () => void
  showSuccessDialog: boolean
  setShowSuccessDialog: (show: boolean) => void
  handleConfirmClone: () => void
  handleGoBackToList: () => void
  isPreCloneDialogShown: boolean
  togglePreCloneDialog: (isShown: boolean) => void
}

export const useCloneAction = (
  status: OrderStatus | null | undefined,
  orderId: number | undefined
): CloneActionReturn => {
  const notify = useNotificationsStore(useShallow((state) => state.enqueueNotification))
  const router = useRouter()
  const navigate = useNavigate()
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [isPreCloneDialogShown, setIsPreCloneDialogShown] = useState(false)
  const canClone = status ? CLONABLE_STATUSES.includes(status as ClonableStatus) : false

  const [{ data, fetching }, clonePurchaseOrder] = useMutation(ClonePurchaseOrderDocument)

  const handlePreClone = useCallback(() => {
    if (!canClone) {
      notify({
        type: NotificationType.Error,
        message: "Purchase Order cannot be cloned in its current state",
      })
      return
    }

    if (!orderId) {
      notify({
        type: NotificationType.Error,
        message: "Order ID is required to clone the purchase order",
      })
      return
    }

    setIsPreCloneDialogShown(true)
  }, [canClone, orderId, notify])

  const handleClone = useCallback(
    (shipInfo?: { shipTo: string }) => {
      if (!orderId) {
        notify({
          type: NotificationType.Error,
          message: "Order ID is required",
        })
        return
      }

      if (fetching) {
        notify({
          type: NotificationType.Info,
          message: "Cloning purchase order...",
        })
      }

      const shipTo = shipInfo?.shipTo ? (JSON.parse(shipInfo.shipTo) as PurchaseOrderShipToFormValue) : null

      clonePurchaseOrder({
        input: {
          orderId,
          ...(shipTo && {
            destinationLocationAssociationId: shipTo.locationAssociationId,
            warehouseAreaId: shipTo.warehouseAreaId,
          }),
        },
      })
      setIsPreCloneDialogShown(false)
    },
    [orderId, clonePurchaseOrder, notify, fetching]
  )

  const handleConfirmClone = useCallback(() => {
    if (data?.order.clone.__typename === "OrderCloneSuccess") {
      notify({
        type: NotificationType.Success,
        message: `Purchase Order ${orderId} successfully cloned as ${data.order.clone.clonedOrder?.orderId}`,
      })

      const newOrderId = data.order.clone.clonedOrder?.orderId?.toString() ?? ""

      navigate({
        to: "/app/procurement/purchase-orders/$purchaseOrderId",
        params: { purchaseOrderId: newOrderId },
      })

      setShowSuccessDialog(false)
    }
  }, [data, navigate, notify, orderId])

  const handleGoBackToList = useCallback(() => {
    router.navigate({
      to: "/app/procurement/purchase-orders",
      search: {
        query: "",
        category: PurchaseOrderSearchCategory.SUPPLIER,
        order: SortingOrder.Desc,
      },
    })

    setShowSuccessDialog(false)
  }, [router])

  useEffect(() => {
    if (data?.order.clone.__typename === "OrderCloneSuccess") {
      setShowSuccessDialog(true)
    }

    if (data?.order.clone.__typename === "OrderCloneFailure") {
      notify({
        type: NotificationType.Error,
        message: data.order.clone.error?.message ?? "Failed to clone purchase order",
      })
    }
  }, [data, notify])

  return {
    canClone,
    handleClone,
    handlePreClone,
    showSuccessDialog,
    setShowSuccessDialog,
    handleConfirmClone,
    handleGoBackToList,
    isPreCloneDialogShown,
    togglePreCloneDialog: setIsPreCloneDialogShown,
  } as const
}
